import { seeProjectPrice } from '@ab-core-functions/user-permission';
import {
    SPECIAL_SHIPPING_COSTS_TYPE,
    SPECIAL_SHIPPING_SKUS
} from '@ab-core/gatsby-theme-core/src/scopes/cart/components/functions';
import type { CartProduct, Query, QueryCartPriceObjectArgs } from '@ab-core/graphql/dist';
import { useAuth } from '@ab-core/hooks';
import type { MappedCart } from '@ab-core/hooks/cartNew/mappers/cartMapper';
import { priceStore } from '@ab-core/store';
import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { getErrorLoggerFunction } from '../utils';
import { CART_PRICE_OBJECT } from './gql/cartPriceObject';

type QueryDataType = Pick<Query, 'cartPriceObject'>;
type OutgoingDataType = QueryDataType['cartPriceObject'];
type IncomingDataType = {
    skip?: boolean;
    projectId?: number;
    cart?: MappedCart;
    hidePrice?: boolean;
};

const onError = getErrorLoggerFunction('cartPriceObject');

export const useCartPriceObject = (props: IncomingDataType) => {
    const { skip = false, projectId = 0, cart, hidePrice = false } = props;
    const [outgoingData, setOutgoingData] = useState<OutgoingDataType>(undefined);

    const { user } = useAuth();
    const canSeeProjectPrice = seeProjectPrice();
    const myCredits = priceStore.getters.useCredits();
    const maxCredits = priceStore.getters.useMaxCredits();
    const coupon = priceStore.getters.useCoupon();

    const credits = myCredits > maxCredits ? maxCredits : myCredits;

    const checkedProjectId = canSeeProjectPrice ? projectId : 0;

    const products = cart?.lineItemsV2?.map((lineItem) => {
        const cartProduct: CartProduct = {
            productId: lineItem.sku,
            quantity: lineItem.quantity,
            pvggType: lineItem.sku?.includes(SPECIAL_SHIPPING_SKUS[SPECIAL_SHIPPING_COSTS_TYPE.PV])
                ? 'PV'
                : lineItem.sku?.includes(SPECIAL_SHIPPING_SKUS[SPECIAL_SHIPPING_COSTS_TYPE.GG])
                ? 'GG'
                : '',
            postalCode:
                (lineItem.sku?.includes(SPECIAL_SHIPPING_SKUS[SPECIAL_SHIPPING_COSTS_TYPE.PV]) &&
                    cart.shippingAddress?.postalCode) ||
                ''
        };

        if (lineItem.chargeNr) {
            cartProduct.chargeNr = parseInt(lineItem.chargeNr);
        }

        return cartProduct;
    });

    const includeShipping = cart?.shippingMethod !== 'PICKUP';

    const isSkip = !products || !user || skip;

    const { data, loading, refetch, error } = useQuery<QueryDataType, QueryCartPriceObjectArgs>(CART_PRICE_OBJECT, {
        variables: {
            coupon,
            credits,
            includeShipping,
            products,
            hidePrice,
            projectId: checkedProjectId
        },
        skip: isSkip,
        onError
    });

    useEffect(() => {
        if (data) {
            setOutgoingData(data.cartPriceObject);
        }
    }, [data]);

    return {
        cartPriceObject: outgoingData,
        error: !!error,
        success: !!data,
        loading,
        refetch
    };
};
